import { Alert, AlertDescription, AlertTitle, Button, Checkbox } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { FileChoice, HelpPopover, NotPermitted, type IDataColumn } from '@/design';
import { ApiError, ProblemType, saveFile, type IEtagged } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type {
  IExportColumn,
  IExportTemplate,
  IFinalistAddition,
  IFinalistFirm,
  IFinalistRemoval,
  IFinalistReport,
  IRankingImportIssue
} from '../../api/ranking-process-contracts';
import { rankingProcessApi } from '../../api/ranking-process-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { NEW_IMPORT, exportLayoutPath, importPath } from '../../paths';
import { countText, rankingRefusalReason } from '../../rankings';
import { refusalMessage } from '../../refusal';
import { Checklist } from '../lists/advanced-filter';
import { sentence } from '../person-hub/said';
import { ColumnRows, sentColumns, useFieldOffers } from './export-columns';
import { fileRefusal, ReportTable } from './import-panel';
import { FactStrip, useRanking } from './ranking';
import { WholeSaveFooter, wholeSaveRefusal, type WholeSaid } from './whole-save';

/**
 * A ranking's Finalists tab (SR-231, RP §11; RNK-15): the finalists sent to their firms and a
 * firm's answer taken back. `rankings.manage`'s, the tab hidden from anyone else, as Import is.
 *
 * **The firms are the ranking's own** (`…/finalists/firms`, each with its finalists' count), chosen
 * rather than typed — "No firm" for those with none, and on FA and TEAMS one "Other/RIA" for every
 * firm off the register, its members named under it.
 *
 * **Out** (SR-444): the chosen firms' workbooks — one XLSX for one firm, else a ZIP, or one XLSX of
 * them all where "One file for the chosen firms" is ticked (SR-505) — or every firm's, saved under
 * the name survey-api gives (`Content-Disposition`), else one of this side's. A combined file takes
 * the one Export layout its firms have: two different ones are said here and refused there.
 * Any status: a published ranking's files are the final list. The files carry the list's saved
 * finalist columns, edited here and saved with the scoring template's version under `If-Match`
 * (the template's filter and columns sent back as read); a download waits while an edit is unsaved,
 * so a file never carries other columns than the ones on screen.
 *
 * **Back**: a firm's returned file (RNK-15, SR-415) — of one firm or, for a combined file, of the
 * firms it holds — checked first (no `If-Match`) — counts, its
 * errors, the corrections it would make and the removals and additions it asks for — and applied
 * second under the record's `ETag`, as the import is. A row left empty or marked `confirm` is the
 * firm's confirmation, recorded by the apply and counted per firm; `remove` takes a finalist out,
 * `add` puts an advisor in at the row's Rank (or the territory's end), the places after moving
 * down — survey-api's one batch. A profile column the firm changed is a `correction` warning: the
 * file never writes an advisor's data, which goes through Import (LST-09), linked. An addition is
 * the firm's confirmation too; one whose Travel reads NO (`not-eligible`), whose profile names
 * another firm (`other-firm`) or who goes into a territory no finalist holds (`new-territory`) is a
 * warning, applied all the same. A removed finalist's confirmation is forgotten. The
 * report shown is the one for the firms and file on screen, so Apply never sends what was not
 * checked. Applying needs a draft — firms review their finalists **before** the ranking is
 * published (Veritant's timeline: before the results go to Forbes) — so a published ranking draws,
 * in place of the upload, that its answers wait for Back to draft…. Its answer's `ETag` is adopted,
 * and the record read again for what the removals changed. A stale `ETag` is said with "Read again"
 * above; `ranking-published` reads the record again, after which the tab says the same.
 */
export function RankingFinalistsTab() {
  const record = useRanking();
  if (!record.permitted) return <NotPermitted what="sending finalists to firms" />;
  return <Firms />;
}

/** The ranking's firms with their finalists and confirmations, read once for both halves — again
 *  after an answer is applied. */
function Firms() {
  const { key, freshness, overview } = useRanking();
  const { data, error } = useAdminData<IFinalistFirm[]>(
    (signal) => rankingProcessApi.finalistFirms(key, signal),
    [freshness, overview?.ranking.version]
  );
  const [unsaved, setUnsaved] = React.useState(false);
  return (
    <div className="grid max-w-5xl gap-6">
      {error && <LoadError message={`The firms could not be read. ${error}`} />}
      <FinalistFiles firms={data} unsaved={unsaved} />
      <FinalistColumns onDirty={setUnsaved} />
      <ReturnedFile firms={data} />
    </div>
  );
}

/** The firms chosen; the firms read again (an applied answer can take one's last finalist away)
 *  keep only the chosen firms they still hold. */
function useChosenFirms(firms: Nullable<IFinalistFirm[]>) {
  const [chosen, setChosen] = React.useState<string[]>([]);
  const [seen, setSeen] = React.useState(firms);
  if (firms !== seen) {
    setSeen(firms);
    if (firms) setChosen(chosen.filter((name) => firms.some((entry) => entry.firm === name)));
  }
  return [chosen, setChosen] as const;
}

/** A file of several firms' name, as survey-api writes it: theirs joined, or the first and how
 *  many more where that is too long. */
function combinedName(firms: string[]): string {
  const joined = firms.join(' + ');
  return joined.length <= 100 || firms.length === 1 ? joined : `${firms[0]} + ${firms.length - 1} more`;
}

/** Firms chosen from the ranking's own, each with how many finalists it holds and, under it, its
 *  layout and the firms a group holds. */
function FirmPicker({
  firms,
  value,
  onChange
}: {
  firms: Nullable<IFinalistFirm[]>;
  value: string[];
  onChange: (firms: string[]) => void;
}) {
  const labelId = React.useId();
  return (
    <div className="grid gap-1 text-sm">
      <span id={labelId} className="text-muted-foreground">
        Firms
      </span>
      <Checklist
        label="Firms"
        choices={(firms ?? []).map((entry) => ({
          code: entry.firm,
          label: firmWords(entry),
          hint: firmHint(entry)
        }))}
        values={value}
        invalid={false}
        searchable
        wide
        disabled={firms === null}
        className="w-72"
        labelId={labelId}
        summary={(codes) => (codes.length === 1 ? codes[0] : `${codes.length} firms`)}
        onChange={(next) => onChange(next.map(String))}
      />
    </div>
  );
}

/** A firm with its finalists and, where survey-api counts them, how many it confirmed. */
function firmWords(entry: IFinalistFirm): string {
  const finalists =
    `${countText(entry.finalists)} ${entry.finalists === 1 ? 'finalist' : 'finalists'}`;
  return entry.confirmed === undefined
    ? `${entry.firm} (${finalists})`
    : `${entry.firm} (${finalists}, ${countText(entry.confirmed)} confirmed)`;
}

/** An option's second line: the firm's Export layout, and the profile firms a group holds where it
 *  is one — Other/RIA's. */
function firmHint(entry: IFinalistFirm): string | undefined {
  const members = entry.members ?? [];
  const group = members.length > 1 || (members.length === 1 && members[0] !== entry.firm);
  const lines = [
    ...(entry.layout ? [`Layout: ${entry.layout}`] : []),
    ...(group ? [members.join(', ')] : [])
  ];
  return lines.length > 0 ? lines.join(' · ') : undefined;
}

/** Where a firm's corrections to an advisor's data go: a new list import of the survey — a link
 *  for `firm_exchange.manage`, Import's own permission, and the word alone for anyone else, whom
 *  the link would only send home. */
function ImportLink({ children }: { children: React.ReactNode }) {
  const { key } = useRanking();
  const imports = useCan()('firm_exchange.manage');
  if (!imports) return <>{children}</>;
  return (
    <Link
      className="underline"
      to={importPath(NEW_IMPORT, `?survey=${encodeURIComponent(key.surveyCode)}`)}
    >
      {children}
    </Link>
  );
}

function FinalistFiles({
  firms,
  unsaved
}: {
  firms: Nullable<IFinalistFirm[]>;
  unsaved: boolean;
}) {
  const { key } = useRanking();
  const imports = useCan()('firm_exchange.manage');
  const [chosen, setChosen] = useChosenFirms(firms);
  const [combine, setCombine] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  // The chosen firms with an Export layout of their own — the Export layouts screen is
  // `firm_exchange.manage`'s, so a link for that alone.
  const laidOut = (firms ?? []).filter((entry) => chosen.includes(entry.firm) && entry.layout);
  // One file of several firms (SR-505) takes the one layout they have; two survey-api refuses.
  const combined = combine && chosen.length > 1;
  const clash = combined && new Set(laidOut.map((entry) => entry.layoutId ?? entry.layout)).size > 1;

  const download = async (some: Nullable<string[]>, together = false) => {
    setBusy(true);
    setRefusal(null);
    try {
      const { file, fileName } = await rankingProcessApi.finalistFiles(key, some, null, together);
      const base = `${key.surveyCode}-${key.list}-${key.year}-finalists`;
      const single = some !== null && (together || some.length === 1);
      saveFile(file, fileName ?? (single ? `${base}-${combinedName(some)}.xlsx` : `${base}.zip`));
    } catch (failure) {
      setRefusal(filesRefusal(failure, some));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Send to firms"
      className="grid gap-3 rounded-md border border-border bg-white p-4"
    >
      <div className="flex items-center gap-1">
        <h2 className="text-base font-semibold text-[var(--color-royal-blue)]">Send to firms</h2>
        <HelpPopover label="About sending to firms">
          <p>
            Sent while the ranking is a draft, before it is published: one workbook per firm, each
            with its finalists, the columns of the finalist file below, and empty Action and Note
            columns. A firm leaves the Action empty or writes <code>confirm</code> to confirm a
            finalist, <code>remove</code> for one who declines, and <code>add</code> on a new row
            for an advisor to add — with the Participant ID, the Territory on a list ranked by
            territory, and a Rank, or none to go last; its answer is applied below.
          </p>
          <p>
            One firm chosen downloads its XLSX; several, or every firm, a ZIP of one XLSX per firm.
            With <em>One file for the chosen firms</em> ticked, the chosen firms go into one XLSX
            — several divisions of one firm, say — and its answer is checked below with the same firms
            chosen. On FA and Teams the firms not on the firm register are one firm, Other/RIA — one
            file and one answer — with the firms it holds under its name.
          </p>
          <p>
            The firm an advisor’s file goes to is the current firm BrokerCheck names, matched to the
            firm register by name or by firm CRD; the firm typed into the profile only where
            BrokerCheck names none. The Firm column holds that register firm — or, for Other/RIA,
            the advisor’s own — and the BrokerCheck firm column BrokerCheck’s name as it stands.
          </p>
        </HelpPopover>
      </div>
      <p className="text-sm text-muted-foreground">
        A correction to an advisor’s data goes through <ImportLink>Import</ImportLink>, not this
        file.
      </p>
      {!imports && <NotPermitted what="importing a firm’s file, where corrections go" />}
      {firms?.some((entry) => entry.confirmed !== undefined) && (
        <ul className="grid gap-0.5 text-sm" aria-label="Firms’ confirmations">
          {firms.map((entry) => (
            <li key={entry.firm} data-firm-confirmed={entry.firm}>
              {entry.firm}: {countText(entry.confirmed ?? 0)} of {countText(entry.finalists)}{' '}
              confirmed
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <FirmPicker firms={firms} value={chosen} onChange={setChosen} />
        <label className="flex h-9 items-center gap-2 text-sm">
          <Checkbox
            checked={combine}
            disabled={busy}
            onCheckedChange={(next) => setCombine(next === true)}
          />
          One file for the chosen firms
        </label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy || unsaved || chosen.length === 0 || clash}
          onClick={() => void download(chosen, combined)}
        >
          {combined ? 'Download one file for the chosen firms' : 'Download the chosen firms’ files'}
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={busy || unsaved}
          onClick={() => void download(null)}
        >
          Download every firm’s file (ZIP)
        </Button>
      </div>
      {laidOut.length > 0 && (
        <ul className="grid gap-0.5 text-xs text-muted-foreground" aria-label="Chosen firms’ layouts">
          {laidOut.map((entry) => (
            <li key={entry.firm} data-firm-layout={entry.firm}>
              {entry.firm} — layout:{' '}
              {imports && entry.layoutId ? (
                <Link className="underline" to={exportLayoutPath(entry.layoutId)}>
                  {entry.layout}
                </Link>
              ) : (
                entry.layout
              )}
            </li>
          ))}
        </ul>
      )}
      {clash && (
        <p className="text-sm text-muted-foreground" data-layouts-clash>
          The chosen firms have different Export layouts, and one file takes one: download their
          files separately, or combine firms that share a layout.
        </p>
      )}
      {unsaved && (
        <p className="text-sm text-muted-foreground" data-columns-unsaved>
          The columns of the finalist file have unsaved changes: save or discard them to download.
        </p>
      )}
      {refusal && (
        <Alert variant="destructive">
          <AlertTitle>Not written</AlertTitle>
          <AlertDescription>{refusal}</AlertDescription>
        </Alert>
      )}
    </section>
  );
}

/**
 * The finalist file's extra columns — the list's, every year's — as List setup edits the scoring
 * template's: the same rows, edited whole and saved whole under the template's `If-Match`, the
 * filter and scoring columns sent back as read. A conflict is said with Reload, which drops the
 * draft. `onDirty` tells the downloads an edit is unsaved.
 */
function FinalistColumns({ onDirty }: { onDirty: (dirty: boolean) => void }) {
  const { key, permitted } = useRanking();
  const { data, error, reload, adopt } = useAdminData<IEtagged<IExportTemplate>>(async (signal) => {
    try {
      return await rankingProcessApi.template(key.surveyCode, key.list, signal);
    } catch (readError) {
      throw new Error(refusalMessage(readError, 'Could not read the columns'));
    }
  });
  const { offerOf } = useFieldOffers(key.surveyCode);
  const [draft, setDraft] = React.useState<Nullable<IExportColumn[]>>(null);
  const [drawn, setDrawn] = React.useState<Nullable<IExportTemplate>>(null);
  const [said, setSaid] = React.useState<Nullable<WholeSaid>>(null);
  const [busy, setBusy] = React.useState(false);
  // A template read or saved replaces the draft — the draft is always of the version shown.
  const template = data?.body ?? null;
  const saved = template?.finalistColumns ?? [];
  if (template !== drawn) {
    setDrawn(template);
    setDraft(template && saved);
  }
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved);
  React.useEffect(() => onDirty(dirty), [dirty, onDirty]);

  const save = async () => {
    if (template === null || draft === null) return;
    if (data?.etag == null) {
      setSaid({
        kind: 'refused',
        text: 'These columns came without their version — read them again.'
      });
      return;
    }
    setBusy(true);
    setSaid(null);
    try {
      const answer = await rankingProcessApi.saveTemplate(key.surveyCode, key.list, data.etag, {
        filter: template.filter,
        columns: sentColumns(template.columns),
        finalistColumns: sentColumns(draft)
      });
      adopt(answer);
      setSaid({ kind: 'saved', text: `Saved — version ${answer.body.version}.` });
    } catch (failure) {
      setSaid(wholeSaveRefusal(failure, 'The columns were refused'));
    } finally {
      setBusy(false);
    }
  };

  const editable = permitted && !busy;
  return (
    <section
      aria-label="Columns of the finalist file"
      className="grid gap-4 rounded-md border border-border bg-white p-4"
    >
      <div className="flex items-center gap-1">
        <h2 className="text-base font-semibold text-[var(--color-royal-blue)]">
          Columns of the finalist file
        </h2>
        <HelpPopover label="About the columns of the finalist file">
          <p>
            Every firm’s file holds Participant ID, Name, Firm, BrokerCheck firm, City, State,
            Territory and Rank, then these columns in this order, then Action and Note. A firm’s
            answer ignores them.
          </p>
          <p>
            A firm with an Export layout of its own also gets that layout’s columns in its file,
            after these. Their figures and survey answers are read from the edition’s snapshot, the
            values the ranking reads; other fields as they stand now. The file takes the layout’s
            number format and gridlines.
          </p>
          <p>
            They belong to the list and apply to every year of it. A survey question or a figure
            reads the edition’s cycle, so a ranking without one takes staff fields alone.
          </p>
        </HelpPopover>
      </div>
      {template === null || draft === null ? (
        error ? (
          <LoadError message={error} />
        ) : (
          <p className="text-sm text-muted-foreground">Reading the columns…</p>
        )
      ) : (
        <>
          <fieldset className="grid gap-2" disabled={!editable}>
            <ColumnRows
              columns={draft}
              offerOf={offerOf}
              empty="No extra column."
              onChange={setDraft}
            />
          </fieldset>
          <WholeSaveFooter
            what="these columns"
            it="them"
            saveLabel="Save the columns"
            notPermitted="changing a list’s finalist columns"
            said={said}
            busy={busy}
            editable={editable}
            dirty={dirty}
            permitted={permitted}
            onSave={() => void save()}
            onDiscard={() => setDraft(saved)}
            onReload={() => void reload().then((read) => read && setSaid(null))}
          />
          {error && (
            <LoadError message={`Could not refresh — showing the columns as last read. ${error}`} />
          )}
        </>
      )}
    </section>
  );
}

/** What a refused download says: a 400's `firms[i]` by the firm's name, a 404 of chosen firms as
 *  the ranking gone (every chosen firm was offered by it). */
function filesRefusal(failure: unknown, some: Nullable<string[]>): string {
  if (failure instanceof ApiError) {
    if (failure.status === 404) {
      return some === null
        ? 'This ranking places nobody to send.'
        : 'No ranking is kept at this address.';
    }
    const problems = failure.problem?.problems ?? [];
    if (some !== null && problems.length > 0) {
      return sentence(
        problems
          .map(({ target, reason }) => {
            // A layout refusal names its firms itself.
            if (target === 'layout') return reason;
            const at = /^firms\[(\d+)\]$/.exec(target);
            const name = at ? some[Number(at[1])] : undefined;
            return `${name === undefined ? target : `“${name}”`}: ${reason}`;
          })
          .join('; ')
      );
    }
  }
  return sentence(refusalMessage(failure, 'The finalists could not be written'));
}

interface IChecked {
  firmsKey: string;
  file: File;
  report: IFinalistReport;
}

function ReturnedFile({ firms }: { firms: Nullable<IFinalistFirm[]> }) {
  const record = useRanking();
  const draft = record.overview?.ranking.status === 'draft';
  const [chosen, setChosen] = useChosenFirms(firms);
  const [file, setFile] = React.useState<Nullable<File>>(null);
  const [checked, setChecked] = React.useState<Nullable<IChecked>>(null);
  const [busy, setBusy] = React.useState<Nullable<'check' | 'apply'>>(null);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const [applied, setApplied] = React.useState<Nullable<string>>(null);

  // The same firms in any order are the same answer.
  const firmsKey = [...chosen].sort().join('\n');
  const report =
    checked && checked.file === file && checked.firmsKey === firmsKey ? checked.report : null;
  const appliable =
    draft &&
    report !== null &&
    !report.applied &&
    report.errors.length === 0 &&
    report.removed + report.added + report.confirmed > 0;
  // The warnings that are a firm's changes to an advisor's data, apart from an addition's own
  // warnings (RNK-15).
  const corrections = report?.warnings.filter((issue) => issue.code === 'correction').length ?? 0;

  const send = async (apply: boolean) => {
    if (!file || chosen.length === 0) return;
    if (apply && record.etag === null) {
      setRefusal('This ranking came without its version — press Refresh.');
      return;
    }
    setBusy(apply ? 'apply' : 'check');
    setRefusal(null);
    setApplied(null);
    try {
      const { body: answer, etag } = await rankingProcessApi.importFinalists(
        record.key,
        chosen,
        file,
        apply && record.etag !== null ? { etag: record.etag } : null
      );
      setChecked({ firmsKey, file, report: answer });
      if (answer.applied) {
        // The applied answer's `ETag` is taken at once; the record is still read again, for the
        // placements and counts the removals changed.
        if (answer.version !== null) record.adopt(etag, answer.version);
        setApplied(
          `Applied to ${record.overview?.ranking.name ?? 'the ranking'} — ` +
            `${countText(answer.removed)} removed, ${countText(answer.added)} added, ` +
            `${countText(answer.confirmed)} confirmed.`
        );
        void record.readAgain();
      }
    } catch (failure) {
      setRefusal(finalistRefusal(failure));
      const reason = rankingRefusalReason(failure);
      if (reason === 'ranking-published') {
        setChecked(null);
        void record.readAgain();
      }
    } finally {
      setBusy(null);
    }
  };

  if (record.overview?.ranking.status === 'published') {
    // survey-api applies a firm's removals to a draft alone (RP §11.2).
    return (
      <section
        aria-label="A firm’s answer"
        className="grid gap-2 rounded-md border border-border bg-white p-4"
      >
        <h2 className="text-base font-semibold text-[var(--color-royal-blue)]">A firm’s answer</h2>
        <p className="text-sm text-muted-foreground" data-answers-need-draft>
          Firms review their finalists before the ranking is published, and their answers are
          applied to the draft. This ranking is published: to apply a firm’s answer now, take it back
          with Back to draft… first.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="A firm’s answer"
      className="grid gap-4 rounded-md border border-border bg-white p-4"
    >
      <h2 className="text-base font-semibold text-[var(--color-royal-blue)]">A firm’s answer</h2>
      <div className="flex flex-wrap items-end gap-3">
        <FirmPicker firms={firms} value={chosen} onChange={setChosen} />
        <FileChoice
          label="Returned file (CSV or XLSX)"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          file={file}
          onChoose={(next) => {
            setFile(next);
            setApplied(null);
            setRefusal(null);
          }}
        />
        <Button
          type="button"
          size="sm"
          disabled={!file || chosen.length === 0 || busy !== null}
          onClick={() => void send(false)}
        >
          {busy === 'check' ? 'Checking…' : 'Check the answer'}
        </Button>
      </div>
      {refusal && (
        <Alert variant="destructive">
          <AlertTitle>Not taken</AlertTitle>
          <AlertDescription>
            <span>{refusal}</span>
          </AlertDescription>
        </Alert>
      )}
      <p role="status" className={applied ? 'text-sm text-[var(--color-teal)]' : 'sr-only'}>
        {applied ?? ''}
      </p>

      {report && (
        <section aria-label="Answer report" className="grid gap-4">
          <FactStrip
            hook="data-report"
            facts={[
              ['rows', 'Rows', countText(report.rows)],
              ['confirmed', 'Confirmed', countText(report.confirmed)],
              ['removed', 'Removed', countText(report.removed)],
              ['added', 'Added', countText(report.added)]
            ]}
          />
          <ReportTable
            title="Errors"
            heading={3}
            rows={report.errors}
            columns={ERROR_COLUMNS}
            hook={['data-row-of', (issue) => issue.code]}
          />
          {corrections > 0 && (
            <p className="text-sm" data-corrections="">
              {countText(corrections)} {corrections === 1 ? 'row changes' : 'rows change'} an
              advisor’s data. This file does not write it — send the corrections through{' '}
              <ImportLink>Import</ImportLink>. The rest of the answer applies as it is.
            </p>
          )}
          <ReportTable
            title="Warnings"
            heading={3}
            rows={report.warnings}
            columns={ERROR_COLUMNS}
            hook={['data-row-of', (issue) => issue.code]}
          />
          <ReportTable
            title="Removals"
            heading={3}
            rows={report.removals}
            columns={REMOVAL_COLUMNS}
            hook={['data-row-of', (removal) => removal.participantId]}
          />
          <ReportTable
            title="Additions"
            heading={3}
            rows={report.additions}
            columns={ADDITION_COLUMNS}
            hook={['data-row-of', (addition) => addition.participantId]}
          />
          {!report.applied && (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                size="sm"
                disabled={!appliable || busy !== null}
                onClick={() => void send(true)}
              >
                {busy === 'apply' ? 'Applying…' : 'Apply the answer'}
              </Button>
              {report.errors.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  Fix the errors in the file and check it again.
                </span>
              )}
            </div>
          )}
        </section>
      )}
    </section>
  );
}

function finalistRefusal(failure: unknown): string {
  if (failure instanceof ApiError && failure.isProblem(ProblemType.revisionConflict)) {
    return 'Someone changed this ranking since this page read it, so nothing was applied. Press Refresh, check the file again, then apply.';
  }
  return fileRefusal(failure, 'No ranking is kept at this address.', 'The file could not be taken');
}

const ERROR_COLUMNS: IDataColumn<IRankingImportIssue & { index: number }>[] = [
  { id: 'row', header: 'Row', numeric: true, cell: (issue) => issue.row ?? '—' },
  { id: 'code', header: 'Code', className: 'font-mono text-xs', cell: (issue) => issue.code },
  { id: 'message', header: 'Message', cell: (issue) => issue.message }
];

const REMOVAL_COLUMNS: IDataColumn<IFinalistRemoval & { index: number }>[] = [
  { id: 'row', header: 'Row', numeric: true, cell: (removal) => removal.row },
  {
    id: 'name',
    header: 'Participant',
    className: 'font-medium',
    cell: (removal) => removal.displayName
  },
  { id: 'territory', header: 'Territory', cell: (removal) => removal.territory ?? '' },
  { id: 'place', header: 'Place', numeric: true, cell: (removal) => removal.place },
  { id: 'note', header: 'Note', cell: (removal) => removal.note ?? '' }
];

const ADDITION_COLUMNS: IDataColumn<IFinalistAddition & { index: number }>[] = [
  { id: 'row', header: 'Row', numeric: true, cell: (addition) => addition.row },
  {
    id: 'name',
    header: 'Participant',
    className: 'font-medium',
    cell: (addition) => addition.displayName
  },
  { id: 'firm', header: 'Firm', cell: (addition) => addition.firm ?? '' },
  { id: 'territory', header: 'Territory', cell: (addition) => addition.territory ?? '' },
  {
    id: 'place',
    header: 'Place',
    numeric: true,
    cell: (addition) => addition.place ?? 'Last'
  },
  { id: 'note', header: 'Note', cell: (addition) => addition.note ?? '' }
];
